import { prepareHeaders } from "./";
import cincolabdataEndpoints from "./cincolabdata.endpoints";

interface ActivationData {
  instance_key: string;
  email: string;
  first_name: string;
  last_name: string;
  postal_code?: string;
  locale: "en" | "fr";
  marketing_opt_in?: boolean;
}

interface SurveyAnswers {
  [key: string]: string | number | boolean;
}

/**
 * Submits an activation to the API
 * @returns Returns a promise that resolves with the email
 */
export const submitActivation = async ({
  instance_key,
  email,
  first_name,
  last_name,
  postal_code,
  locale,
}: ActivationData): Promise<string> => {
  try {
    const response = await fetch(
      cincolabdataEndpoints.public.form(instance_key),
      {
        method: "POST",
        headers: prepareHeaders(),
        mode: "cors",
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          answers: {
            "FIRST NAME": first_name,
            "LAST NAME": last_name,
            ZIP: postal_code,
            Q799E: "0799",
            A799E: "A",
            Q799M: "0799",
            A799M: "E",
            Q127: "0127",
            "A127 LANGUAGE": locale === "en" ? "C" : "B",
          },
        }),
        redirect: "follow",
      },
    );

    const data = await response.json();
    if (response.status !== 200) {
      throw new Error(`Failed to create activation: ${data.error}`);
    }

    return data.email;
  } catch (error) {
    throw new Error(
      `SubmitActivation: Failed to create activation. ${(error as Error).message}`,
    );
  }
};

/**
 * Submits a survey to the API with retry mechanism
 * @param instance_key - The instance key
 * @param email - The email
 * @param answers - The answers
 * @param retryCount - Number of retries attempted (internal use)
 * @returns Returns a promise that resolves with the survey ID
 */
export const submitSurvey = async (
  instance_key: string,
  email: string,
  answers: SurveyAnswers,
  retryCount = 0,
): Promise<string> => {
  const maxRetries = 2;

  // Validate required parameters
  if (!instance_key) {
    throw new Error("SubmitSurvey: Instance key is required");
  }
  if (!email) {
    throw new Error("SubmitSurvey: Email is required");
  }
  if (!answers) {
    throw new Error("SubmitSurvey: Answers are required");
  }

  // Check if API URL is configured
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    throw new Error(
      "SubmitSurvey: API URL is not configured. Please check environment variables.",
    );
  }

  // Check if API token is configured
  const apiToken = import.meta.env.VITE_API_TOKEN;
  if (!apiToken) {
    throw new Error(
      "SubmitSurvey: API token is not configured. Please check environment variables.",
    );
  }

  const endpoint = cincolabdataEndpoints.public.form(instance_key);
  const logs: string[] = [];

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: prepareHeaders(),
      mode: "cors",
      body: JSON.stringify({
        email: email.toLowerCase().trim(),
        answers: {
          ...answers,
          Q2: "0002",
          COUNTRY: "CAN",
          Q75: "0075",
          "A75 MAKE": "FD",
          Q1439: "1439",
        },
      }),
      redirect: "follow",
    });

    const data = await response.json();

    if (response.status !== 200) {
      throw new Error(
        `Failed to submit survey: (status ${response.status}) ${JSON.stringify(data)}`,
      );
    }

    return data.id;
  } catch (error) {
    logs.push(`SubmitSurvey: Fetch error details (attempt ${retryCount + 1}): {
      message: ${(error as Error).message},
      name: ${(error as Error).name},
      stack: ${(error as Error).stack},
      endpoint: ${endpoint},
      apiUrl: ${apiUrl},
      instance_key: ${instance_key},
      email: ${email.toLowerCase().trim()},
      retryCount: ${retryCount}
    }`);

    if (
      retryCount < maxRetries &&
      ((error as Error).message.includes("Load failed") ||
        (error as Error).message.includes("Failed to fetch") ||
        (error as Error).message.includes("NetworkError") ||
        (error as Error).name === "TypeError")
    ) {
      logs.push(`SubmitSurvey: Retrying in ${(retryCount + 1) * 1000}ms...`);
      await new Promise((resolve) =>
        setTimeout(resolve, (retryCount + 1) * 1000),
      );
      return submitSurvey(instance_key, email, answers, retryCount + 1);
    }

    throw new Error(
      `SubmitSurvey failed after ${retryCount + 1} attempts: ${(error as Error).message} (endpoint: ${endpoint}) ${logs.join("\n")}`,
    );
  }
};

/**
 * Test API connectivity to help diagnose network issues
 * @returns Returns true if API is reachable, false otherwise
 */
export const testApiConnectivity = async (): Promise<boolean> => {
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    console.error("testApiConnectivity: VITE_API_URL is not configured");
    return false;
  }

  try {
    // Test basic connectivity with a HEAD request
    const response = await fetch(apiUrl, {
      method: "HEAD",
      mode: "cors",
      cache: "no-cache",
    });

    console.log("testApiConnectivity: API response status:", response.status);
    return response.ok;
  } catch (error) {
    console.error("testApiConnectivity: Failed to connect to API:", error);
    return false;
  }
};
